import React, { useEffect, useState, Component } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
/**
 * Google Auth Debug Component
 * Use this to test and debug Google OAuth integration
 * Add to any page temporarily: <GoogleAuthDebug />
 */
export function GoogleAuthDebug() {
  const {
    user,
    signInWithGoogle
  } = useAuth();
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  useEffect(() => {
    checkSession();
  }, []);
  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };
  const checkSession = async () => {
    const {
      data: {
        session
      }
    } = await supabase.auth.getSession();
    setSession(session);
    addLog(session ? '✅ Active session found' : '❌ No active session');
  };
  const testGoogleSignIn = async () => {
    setLoading(true);
    addLog('🔐 Initiating Google Sign-In...');
    try {
      await signInWithGoogle();
      addLog('✅ Google Sign-In initiated (redirecting...)');
    } catch (error: any) {
      addLog(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  const testDirectOAuth = async () => {
    setLoading(true);
    addLog('🔐 Testing direct OAuth call...');
    try {
      const {
        data,
        error
      } = await supabase.auth.signInWithOAuth({
        provider: 'google'
      });
      if (error) {
        addLog(`❌ OAuth Error: ${error.message}`);
      } else {
        addLog(`✅ OAuth initiated: ${data.url}`);
        addLog('🔄 Redirecting to Google...');
      }
    } catch (error: any) {
      addLog(`❌ Unexpected error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  const checkProviders = async () => {
    addLog('🔍 Checking available providers...');
    try {
      // This will show if Google is enabled in Supabase
      const {
        data,
        error
      } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          skipBrowserRedirect: true
        }
      });
      if (error) {
        addLog(`❌ Provider check failed: ${error.message}`);
      } else {
        addLog('✅ Google provider is available');
      }
    } catch (error: any) {
      addLog(`❌ Error checking providers: ${error.message}`);
    }
  };
  const clearLogs = () => {
    setLogs([]);
  };
  return <div className="fixed bottom-4 right-4 w-96 bg-white rounded-xl shadow-2xl border-2 border-purple-500 p-4 max-h-[600px] overflow-y-auto z-50" data-id="element-559">
      <div className="flex items-center justify-between mb-4" data-id="element-560">
        <h3 className="font-bold text-lg" data-id="element-561">🧪 Google Auth Debug</h3>
        <button onClick={clearLogs} className="text-xs text-gray-500 hover:text-gray-700" data-id="element-562">
          Clear Logs
        </button>
      </div>

      {/* Status */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg" data-id="element-563">
        <p className="text-sm font-medium mb-2" data-id="element-564">Current Status:</p>
        <div className="space-y-1 text-xs" data-id="element-565">
          <p data-id="element-566">
            Session:{' '}
            <span className={session ? 'text-green-600' : 'text-red-600'} data-id="element-567">
              {session ? '✅ Active' : '❌ None'}
            </span>
          </p>
          <p data-id="element-568">
            User:{' '}
            <span className={user ? 'text-green-600' : 'text-red-600'} data-id="element-569">
              {user ? `✅ ${user.email}` : '❌ Not logged in'}
            </span>
          </p>
          <p data-id="element-570">
            Supabase URL:{' '}
            <span className="text-blue-600 break-all" data-id="element-571">
              {supabase.supabaseUrl}
            </span>
          </p>
        </div>
      </div>

      {/* User Info */}
      {user && <div className="mb-4 p-3 bg-green-50 rounded-lg" data-id="element-572">
          <p className="text-sm font-medium mb-2" data-id="element-573">User Info:</p>
          <div className="space-y-1 text-xs" data-id="element-574">
            <p data-id="element-575">Email: {user.email}</p>
            <p data-id="element-576">Name: {user.user_metadata?.fullName || 'Not set'}</p>
            <p data-id="element-577">Provider: {user.app_metadata?.provider || 'Unknown'}</p>
          </div>
        </div>}

      {/* Test Buttons */}
      <div className="space-y-2 mb-4" data-id="element-578">
        <button onClick={testGoogleSignIn} disabled={loading} className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50" data-id="element-579">
          {loading ? 'Testing...' : 'Test Google Sign-In (Context)'}
        </button>

        <button onClick={testDirectOAuth} disabled={loading} className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50" data-id="element-580">
          Test Direct OAuth Call
        </button>

        <button onClick={checkProviders} disabled={loading} className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50" data-id="element-581">
          Check Google Provider Status
        </button>

        <button onClick={checkSession} className="w-full bg-green-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-green-700" data-id="element-582">
          Refresh Session Status
        </button>
      </div>

      {/* Logs */}
      <div className="bg-gray-900 text-green-400 p-3 rounded-lg font-mono text-xs max-h-48 overflow-y-auto" data-id="element-583">
        {logs.length === 0 ? <p className="text-gray-500" data-id="element-584">No logs yet. Click a button to test.</p> : logs.map((log, index) => <p key={index} data-id="element-585">{log}</p>)}
      </div>

      {/* Quick Links */}
      <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-586">
        <p className="text-xs text-gray-600 mb-2" data-id="element-587">Quick Links:</p>
        <div className="space-y-1" data-id="element-588">
          <a href="https://supabase.com/dashboard/project/neadfoxscgjeekvabnoz/auth/providers" target="_blank" rel="noopener noreferrer" className="block text-xs text-blue-600 hover:underline" data-id="element-589">
            → Supabase Auth Providers
          </a>
          <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="block text-xs text-blue-600 hover:underline" data-id="element-590">
            → Google Cloud Console
          </a>
        </div>
      </div>
    </div>;
}